import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';
import {
  FaceSlightlySmiling,
  ChevronRight
} from 'lucide-react';

const NAV_GROUPS = {
  REPORTING: {
    label: 'REPORTING',
    subItems: [
      { key: 'REPORTING_OVERVIEW', label: 'OVERVIEW', path: '/admin/reports' },
      { key: 'REPORTING_NEWSLETTER', label: 'NEWSLETTER', path: '/admin/reports/newsletter' },
      { key: 'REPORTING_CAMPAIGNS', label: 'CAMPAIGN REPORTS', path: '/admin/reports/campaigns' },
    ],
  },
  INDEMNITY: {
    label: 'INDEMNITY',
    subItems: [
      { key: 'INDEMNITY_LANDING', label: 'LANDING PAGE', path: '/admin/indemnity-landing-pages' },
      { key: 'INDEMNITY', label: 'REPORT', path: '/admin/indemnity-submissions' },
    ],
  },
};

const NAV_ITEMS = [
  'CAMPAIGN',
  'MECHANICS',
  'REPORTING',
  'FORMS',
  'PRODUCTS',
  'TEMPLATES',
  'CONTACT',
  'MEMBERSHIP',
  'INDEMNITY',
];

function NavGroupMenu({ config, activeSection, btnClasses, subMenuBtnClasses, onNavigate }) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);
  const isActive = config.subItems.some((sub) => sub.key === activeSection);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSubNav = (subItem) => {
    onNavigate(subItem.path);
    setOpen(false);
  };

  return (
    <div ref={menuRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        aria-haspopup="true"
        className={`${btnClasses(isActive)} flex items-center justify-between gap-1`}
      >
        <span>{config.label}</span>
        <ChevronRight
          className={`w-3.5 h-3.5 shrink-0 transition-transform ${open ? 'rotate-90' : ''}`}
        />
      </button>
      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 min-w-full rounded-xl border border-white/30 bg-[#133634] py-1 shadow-lg">
          {config.subItems.map((subItem) => (
            <button
              key={subItem.key}
              type="button"
              onClick={() => handleSubNav(subItem)}
              className={subMenuBtnClasses(activeSection === subItem.key)}
            >
              {subItem.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function AdminSidebar({ activeSection, variant = 'sidebar' }) {
  const navigate = useNavigate();

  const isDrawer = variant === 'drawer';

  const handleNavigation = (item) => {
    if (item === 'CAMPAIGN') navigate('/admin/campaigns');
    else if (item === 'PAGE') navigate('/admin/campaigns');
    else if (item === 'MECHANICS') navigate('/admin/mechanics');
    else if (item === 'FORMS') navigate('/admin/forms');
    else if (item === 'PRODUCTS') navigate('/admin/products');
    else if (item === 'TEMPLATES') navigate('/admin/templates');
    else if (item === 'CONTACT') navigate('/admin/contact-messages');
    else if (item === 'MEMBERSHIP') navigate('/admin/membership');
    else if (item === 'USERS') navigate('/admin/users');
  };

  const handleLogout = () => {
    authService.logout();
    navigate('/login');
  };

  const containerClasses = isDrawer
    ? 'flex flex-col py-8 px-6 h-full text-white'
    : 'hidden md:flex col-span-2 flex-col py-12 px-6 h-full';

  const dividerClasses = isDrawer ? 'my-2 w-full border-t border-white/30' : 'my-2 w-full border-t border-white/30';

  const btnClasses = (isActive) =>
    isDrawer
      ? `py-2 px-3 text-xs font-medium rounded-xl border transition-all duration-300 w-full ${
          isActive
            ? 'bg-white text-gray-800 border-white'
            : 'bg-transparent text-white border-white hover:bg-white/10'
        }`
      : `py-2 px-3 text-xs font-medium rounded-xl border transition-all duration-300 ${
          isActive
            ? 'bg-white text-gray-800 border-white w-[calc(100%+20px)]'
            : 'bg-transparent text-white border-white hover:bg-white/10 w-full'
        }`;

  const subMenuBtnClasses = (isActive) =>
    `block w-full text-left py-2 px-3 text-xs font-medium rounded-lg transition-colors ${
      isActive ? 'bg-white text-gray-800' : 'text-white hover:bg-white/10'
    }`;

  const logoutBtnClasses = isDrawer
    ? 'py-2 px-3 text-xs font-medium rounded-xl border transition-all duration-300 bg-transparent text-white border-white hover:bg-white/10 w-full'
    : 'py-2 px-3 text-xs font-medium rounded-xl border transition-all duration-300 bg-transparent text-white border-white hover:bg-white/10 w-full';

  const menuLabels = {
    CAMPAIGN: 'CAMPAIGN',
    MECHANICS: 'MECHANICS',
    FORMS: 'FORMS',
    PRODUCTS: 'PRODUCTS',
    TEMPLATES: 'TEMPLATES',
    CONTACT: 'CONTACT',
    MEMBERSHIP: 'MEMBERSHIP',
  };

  return (
    <div className={containerClasses}>
      <div className="flex-1" />

      <div className="w-full max-w-[150px]">
        {NAV_ITEMS.map((item, index) => (
          <div key={item} className="w-full relative">
            {NAV_GROUPS[item] ? (
              <NavGroupMenu
                config={NAV_GROUPS[item]}
                activeSection={activeSection}
                btnClasses={btnClasses}
                subMenuBtnClasses={subMenuBtnClasses}
                onNavigate={navigate}
              />
            ) : (
              <button
                type="button"
                onClick={() => handleNavigation(item)}
                className={btnClasses(activeSection === item)}
              >
                {menuLabels[item] || item}
              </button>
            )}
            {index < NAV_ITEMS.length - 1 && <div className={dividerClasses} />}
          </div>
        ))}
      </div>

      <div className="flex-1" />

      <div className="w-full max-w-[150px] space-y-4">
        <div className={`w-16 h-16 rounded-full flex items-center justify-center shadow-lg ${isDrawer ? 'bg-white' : 'bg-white'}`}>
          <FaceSlightlySmiling className={`w-10 h-10 ${isDrawer ? 'text-gray-800' : 'text-gray-800'}`} />
        </div>

        <button
          type="button"
          onClick={() => handleNavigation('USERS')}
          className={btnClasses(activeSection === 'USERS')}
        >
          USERS
        </button>

        <div className={dividerClasses} />

        <button type="button" onClick={handleLogout} className={logoutBtnClasses}>
          LOGOUT
        </button>
      </div>
    </div>
  );
}

export default AdminSidebar;
